import React, { useMemo, useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Tabs,
  Tab,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  IconButton,
  Box,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Chip,
  Snackbar,
  Collapse
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DoneAll, ArrowBackIosNew, Check, NotificationsNone, InfoOutlined, ExpandMore, ExpandLess, Undo } from '@mui/icons-material';
import Navbar from '../shared/Navbar';
import FilterHeader from '../dashboard/FilterHeader';
import { FILTER_VALUES, NOTIFICATION_EVENT_TYPES, NOTIFICATION_SCREEN, NOTIFICATION_SORT, PAGINATION } from '../../shared/appConstants';
import { COLORS } from '../../shared/colors';
import { fetchNotificationsByUser, resetNotificationsTab, markNotificationsRead, setUnreadCount, applyNotificationReadState } from '../../store/notificationSlice';
import { fetchProjectDropdownsByUser } from '../../store/ticketSlice';
import { groupNotificationsByTicket } from './notificationUtils';
import { api, ENDPOINTS } from '../../services';
import { toast } from 'react-toastify';
import groupByIcon from '../../assets/images/groupby_icon.svg';
import {
  getInitial,
  getCommentThreadKey,
  renderNotificationMessage,
  buildCommentSummary,
  deriveAssignmentChange,
  deriveCommentChange,
  deriveWorklogChange,
  hasNotificationHtmlContent,
  renderNotificationTextContent
} from './notificationUtils';
import './Notifications.scss';


const COMMENT_EVENT_TYPES = new Set([
  NOTIFICATION_EVENT_TYPES.COMMENT,
  NOTIFICATION_EVENT_TYPES.COMMENT_REPLY
]);

const DEFAULT_NOTIFICATION_TAB = NOTIFICATION_SCREEN.TABS.UNREAD;
const DEFAULT_NOTIFICATION_GROUP_BY = NOTIFICATION_SCREEN.GROUP_BY.TASK;
const DEFAULT_NOTIFICATION_SORT = NOTIFICATION_SCREEN.SORT.RECENT;

const ClampedText = ({ children, className, lineClamp, isExpanded, onToggle, t }) => {
  const ref = React.useRef(null);
  const [overflows, setOverflows] = React.useState(false);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || isExpanded) {
      setOverflows(false);
      return;
    }
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [children, isExpanded, lineClamp]);
  return (
    <span className="notifications-expandable-text-block">
      <span
        ref={ref}
        className={className}
        style={!isExpanded ? { display: '-webkit-box', WebkitLineClamp: lineClamp, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'normal' } : undefined}
      >
        {children}
      </span>
      {(overflows || isExpanded) && (
        <Button size="small" onClick={onToggle} startIcon={isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />} className="notifications-read-toggle">
          {isExpanded ? t('settings.header.readLess', 'Read Less') : t('settings.header.readMore', 'Read More')}
        </Button>
      )}
    </span>
  );
};

const Notifications = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, accessToken } = useSelector((state) => state.auth);
  const { itemsByTab, paginationByTab, unreadCount, unreadCountByProject } = useSelector((state) => state.notifications);
  const { dropdowns, projectDropdownsByUser, projectDropdownsByUserLoading } = useSelector((state) => state.tickets || {});
  const { users } = useSelector((state) => state.users || {});
  const [activeTab, setActiveTab] = useState(NOTIFICATION_SCREEN.TABS.UNREAD);
  const selectedProjectFromTickets = useSelector((state) => state.tickets?.filters?.selectedProject);
  const [selectedProject, setSelectedProject] = useState(() => selectedProjectFromTickets || FILTER_VALUES.ALL);

  useEffect(() => {
    if (selectedProjectFromTickets) {
      setSelectedProject(selectedProjectFromTickets);
    }
  }, [selectedProjectFromTickets]);

  const [groupBy, setGroupBy] = useState(NOTIFICATION_SCREEN.GROUP_BY_OPTIONS[0].value);
  const [sortBy, setSortBy] = useState(NOTIFICATION_SCREEN.SORT_OPTIONS[0].value);
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [completedNotifications, setCompletedNotifications] = useState(new Set());
  const [isReadConfirmOpen, setIsReadConfirmOpen] = useState(false);
  const [isReadConfirmLoading, setIsReadConfirmLoading] = useState(false);
  const [pendingReadIds, setPendingReadIds] = useState([]);
  const [pendingReadContext, setPendingReadContext] = useState({ scope: 'page' });
  const previousCompletedRef = useRef(null);
  const [showNewBanner, setShowNewBanner] = useState(false);
  const [newSinceLastLoad, setNewSinceLastLoad] = useState(0);
  const [expandedTextRows, setExpandedTextRows] = useState({});
  const loadMoreRef = useRef(null);
  const mainRef = useRef(null);
  const fixedSectionRef = useRef(null);
  const pendingBaselineResetRef = useRef(true);
  const userId = user?.id || user?.user_id || '';
  const [hasShownDateFilterInfo, setHasShownDateFilterInfo] = useState(false);
  const [showDateFilterInfoDialog, setShowDateFilterInfoDialog] = useState(false);
  const [pendingDateInputRef, setPendingDateInputRef] = useState(null);
  const [expandedManagerGroups, setExpandedManagerGroups] = useState({});

  const hasSelectedProject = selectedProject
    && String(selectedProject).toLowerCase() !== String(FILTER_VALUES.ALL).toLowerCase();
  const currentUnreadCount = hasSelectedProject
    ? (unreadCountByProject?.[selectedProject] || 0)
    : unreadCount;

  const toggleManagerGroup = useCallback((key) => {
    setExpandedManagerGroups(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);
  const [scrollRegionHeight, setScrollRegionHeight] = useState(0);
  const [undoSnackbar, setUndoSnackbar] = useState({ open: false, ids: [], count: 0 });
  const projectDropdownsFetchedRef = useRef(false);

  const activeItems = useMemo(() => (
    itemsByTab?.[activeTab] || []
  ), [activeTab, itemsByTab]);

  // Build mention users list from available sources
  const mentionUsers = useMemo(() => {
    const merged = [];
    if (Array.isArray(assigneeOptions)) merged.push(...assigneeOptions);
    if (Array.isArray(dropdowns?.assignees)) merged.push(...dropdowns.assignees);
    if (Array.isArray(users)) merged.push(...users);
    if (merged.length === 0) return [];
    const unique = new Map();
    merged.forEach((user) => {
      if (!user) return;
      const id = user.id || user.user_id || user.value || user.email || user.name;
      const name = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.label;
      if (!name) return;
      const key = id || name;
      if (unique.has(key)) return;
      unique.set(key, { id: id || name, name });
    });
    return Array.from(unique.values());
  }, [assigneeOptions, dropdowns?.assignees, users]);

  const sourceGroups = useMemo(() => {
    // Check if data is already grouped from API
    if (activeItems.length > 0 && (activeItems[0]?.ticket_id || activeItems[0]?.sprint_id || activeItems[0]?.group_by) && activeItems[0]?.items) {
      return activeItems.flatMap(group => {
        const firstItemEventType = group.items?.[0]?.event_type;
        const isReminderGroup = firstItemEventType === NOTIFICATION_EVENT_TYPES.STANDUP_NOT_UPDATED_REMINDER || firstItemEventType === NOTIFICATION_EVENT_TYPES.TASK_STAGNATION_REMINDER;
        const baseGroup = {
        id: group.ticket_id || group.sprint_id || `group-${Math.random()}`,
        isSprintGroup: !!group.sprint_id || (firstItemEventType === 'SPRINT_CREATED'),
        isReminderGroup,
        ticketNumber: group.items?.[0]?.ticket_number || '',
        title: group.items?.[0]?.ticket_title || group.items?.[0]?.sprint_name || group.items?.[0]?.message || '',
        assignee: group.items?.[0]?.actor ? {
          id: group.items[0].actor.id,
          name: `${group.items[0].actor.first_name || ''} ${group.items[0].actor.last_name || ''}`.trim()
        } : null,
        notifications: group.items.flatMap(item => {
          const itemCommentChange = deriveCommentChange(item.changes, item.message || item.parent_comment || '');

          // Handle items with replies (for comment grouping or task grouping)
          if (item.replies && Array.isArray(item.replies) && item.replies.length > 0) {
            const summaryNotification = {
              id: `summary-${item.parent_comment_id || item.ticket_id}`,
              event_type: 'COMMENT_SUMMARY',
              actorName: `${item.replies[0]?.actor?.first_name || ''} ${item.replies[0]?.actor?.last_name || ''}`.trim(),
              actorId: item.replies[0]?.actor?.id || item.actor?.id,
              commentText: item.parent_comment || '',
              replyToText: item.parent_comment || '',
              commentId: item.parent_comment_id || '',
              commentThreadId: item.parent_comment_id || item.ticket_id,
              replyCount: item.replies.length,
              createdAt: item.replies[0]?.createdAt || new Date().toISOString(),
              isRead: item.replies.every(r => r.is_read),
              actionLabelKey: 'notifications.commentSummary.action',
              message: item.message || '',
              isSummary: true
            };

            const replyNotifications = item.replies.map(reply => {
              const replyCommentChange = deriveCommentChange(reply.changes, reply.message || '');
              return {
                id: reply.id,
                event_type: reply.event_type,
                actorName: `${reply.actor?.first_name || ''} ${reply.actor?.last_name || ''}`.trim(),
                actorId: reply.actor?.id,
                commentId: reply.id,
                commentThreadId: item.parent_comment_id || reply.parent_comment_id || reply.id,
                replyToId: item.parent_comment_id || reply.parent_comment_id || '',
                replyText: replyCommentChange.text,
                commentText: replyCommentChange.text,
                replyToText: item.parent_comment || reply.parent_comment || (!replyCommentChange.isRemoved ? reply.changes?.old_value || '' : ''),
                createdAt: reply.createdAt,
                isRead: reply.is_read,
                actionLabelKey: replyCommentChange.actionLabelKey || 'notifications.actions.replied',
                message: reply.message
              };
            });

            return [summaryNotification, ...replyNotifications];
          }
          
          // Skip items without id (parent containers already processed above)
          if (!item.id) {
            return [];
          }
          
          // Handle regular items
          const normalized = {
            id: item.id,
            event_type: item.event_type,
            commentId: item.id,
            commentThreadId: item.parent_comment_id || item.id,
            replyToId: item.parent_comment_id || '',
            actorName: `${item.actor?.first_name || ''} ${item.actor?.last_name || ''}`.trim(),
            actorId: item.actor?.id,
            message: item.message,
            createdAt: item.createdAt,
            isRead: item.is_read,
            changes: item.changes,
            mentionedUserName: item.mentioned_user_name,
            mentionedUserId: item.mentioned_user_id,
            batch_count: item.batch_count
          };

          // Map action labels based on event type
          const ACTION_LABEL_MAP = {
            'STATUS_CHANGE': 'notifications.actions.statusChanged',
            'ASSIGNMENT': 'notifications.actions.taskAssigned',
            'WORKLOG': 'notifications.actions.worklogUpdated',
            'COMMENT': 'notifications.actions.commented',
            'COMMENT_REPLY': 'notifications.actions.replied',
            'COMMENT_MENTION': 'notifications.actions.mentioned',
            'DESC_MENTION': 'notifications.actions.mentioned',
            'SPRINT_CREATED': 'notifications.actions.sprintCreated'
          };

          normalized.actionLabelKey = ACTION_LABEL_MAP[item.event_type] || '';

          // Add specific fields based on event type
          if (item.event_type === NOTIFICATION_EVENT_TYPES.STANDUP_NOT_UPDATED_REMINDER || item.event_type === NOTIFICATION_EVENT_TYPES.TASK_STAGNATION_REMINDER) {
            let itemChanges = item.changes || {};
            if (typeof itemChanges === 'string') {
              try {
                itemChanges = JSON.parse(itemChanges);
              } catch (e) {}
            }

            normalized.actorName = user?.name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'You';
            normalized.actorId = user?.id || user?.user_id;
            normalized.actionLabel = item.event_type === NOTIFICATION_EVENT_TYPES.STANDUP_NOT_UPDATED_REMINDER 
              ? t('notifications.standupNotUpdated') 
              : t('notifications.taskStagnationAlert');
            
            const isManagerSummary = item.manager_summary || itemChanges.manager_summary;
            const projectMatch = item.message?.match(/in\s+(.*?)(?:\s+(?:require|have|are|is)|$)/i);
            const projectName = projectMatch ? projectMatch[1] : '';
            
            // Check if payload is the STANDUP_NOT_UPDATED_REMINDER format with pending_users
            if (Array.isArray(itemChanges.pending_users)) {
              let totalTickets = 0;
              
              if (isManagerSummary) {
                normalized.managerGroups = itemChanges.pending_users.map(userObj => {
                  const tickets = userObj.tickets || [];
                  totalTickets += tickets.length;
                  return {
                    assignee: userObj.name || t('common.unassigned'),
                    tickets: tickets.map((ticket, idx) => ({
                      id: `${item.id}-${userObj.name}-ticket-${ticket.ticket_id || idx}`,
                      event_type: NOTIFICATION_EVENT_TYPES.REMINDER_TICKET,
                      ticketNumber: ticket.ticket_number,
                      title: ticket.title,
                      ticketId: ticket.ticket_id,
                      isRead: item.is_read,
                      createdAt: item.createdAt,
                      missingUpdates: ticket.missing_updates,
                      endDate: ticket.end_date,
                      hideAssignees: true
                    }))
                  };
                });
                normalized.bulkActions = []; // prevent flat rendering
              } else {
                let allTickets = [];
                itemChanges.pending_users.forEach(u => {
                  if (u.tickets) allTickets = allTickets.concat(u.tickets);
                });
                totalTickets = allTickets.length;
                normalized.bulkActions = allTickets.map((ticket, idx) => ({
                  id: `${item.id}-ticket-${ticket.ticket_id || idx}`,
                  event_type: NOTIFICATION_EVENT_TYPES.REMINDER_TICKET,
                  ticketNumber: ticket.ticket_number,
                  title: ticket.title,
                  ticketId: ticket.ticket_id,
                  isRead: item.is_read,
                  createdAt: item.createdAt,
                  missingUpdates: ticket.missing_updates,
                  endDate: ticket.end_date
                }));
              }

              if (totalTickets > 0) {
                const ticketsText = `${totalTickets} ticket${totalTickets > 1 ? 's' : ''}${projectName ? ` in ${projectName}` : ''}`;
                normalized.actionTargetText = isManagerSummary
                  ? t('notifications.managerSummaryTarget', { ticketsText })
                  : t('notifications.userSummaryTarget', { ticketsText });
              } else {
                normalized.actionTargetText = item.message;
              }
            } else {
              // TASK_STAGNATION_REMINDER flat format or generic tickets array
              let ticketsArray = [];
              if (Array.isArray(itemChanges.tickets)) ticketsArray = itemChanges.tickets;
              else if (Array.isArray(item.tickets)) ticketsArray = item.tickets;
              else if (Array.isArray(item.items)) ticketsArray = item.items;
              else if (Array.isArray(item.data?.tickets)) ticketsArray = item.data.tickets;
              else if (Array.isArray(item.changes)) ticketsArray = item.changes;
              else if (Array.isArray(itemChanges)) ticketsArray = itemChanges;
              else {
                for (const key in itemChanges) {
                  if (Array.isArray(itemChanges[key])) {
                    ticketsArray = itemChanges[key];
                    break;
                  }
                }
                if (ticketsArray.length === 0) {
                  for (const key in item) {
                    if (Array.isArray(item[key]) && item[key][0]?.ticket_number) {
                      ticketsArray = item[key];
                      break;
                    }
                  }
                }
              }

              const ticketCount = ticketsArray.length || item.message?.match(/(\d+)/)?.[1] || '';
              if (ticketCount) {
                const ticketsText = `${ticketCount} ticket${ticketCount > 1 ? 's' : ''}${projectName ? ` in ${projectName}` : ''}`;
                normalized.actionTargetText = isManagerSummary
                  ? t('notifications.managerSummaryTarget', { ticketsText })
                  : t('notifications.userSummaryTarget', { ticketsText });
              } else {
                normalized.actionTargetText = item.message;
              }

              if (ticketsArray.length > 0) {
                if (isManagerSummary) {
                  const assigneeMap = new Map();
                  ticketsArray.forEach((ticket, idx) => {
                    const assignees = Array.isArray(ticket.assignees) && ticket.assignees.length > 0 ? ticket.assignees : [t('common.unassigned')];
                    assignees.forEach(assignee => {
                      if (!assigneeMap.has(assignee)) assigneeMap.set(assignee, []);
                      assigneeMap.get(assignee).push(ticket);
                    });
                  });
                  
                  normalized.managerGroups = Array.from(assigneeMap.entries()).map(([assignee, tickets]) => ({
                    assignee,
                    tickets: tickets.map((ticket, idx) => ({
                      id: `${item.id}-${assignee}-ticket-${ticket.ticket_id || idx}`,
                      event_type: NOTIFICATION_EVENT_TYPES.REMINDER_TICKET,
                      ticketNumber: ticket.ticket_number,
                      title: ticket.title,
                      ticketId: ticket.ticket_id,
                      isRead: item.is_read,
                      createdAt: item.createdAt,
                      missingUpdates: ticket.missing_updates,
                      endDate: ticket.end_date,
                      hideAssignees: true
                    }))
                  }));
                  normalized.bulkActions = []; // prevent flat rendering
                } else {
                  normalized.bulkActions = ticketsArray.map((ticket, idx) => ({
                    id: `${item.id}-ticket-${ticket.ticket_id || idx}`,
                    event_type: NOTIFICATION_EVENT_TYPES.REMINDER_TICKET,
                    ticketNumber: ticket.ticket_number,
                    title: ticket.title,
                    ticketId: ticket.ticket_id,
                    isRead: item.is_read,
                    createdAt: item.createdAt,
                    missingUpdates: ticket.missing_updates,
                    endDate: ticket.end_date,
                    assignees: ticket.assignees
                  }));
                }
              }
            }
          }
          if (item.event_type === 'STATUS_CHANGE' && item.changes) {
            normalized.fromStatus = item.changes.old_value || '';
            normalized.toStatus = item.changes.new_value || '';
          }

          if (item.event_type === 'ASSIGNMENT' && item.changes) {
            const assignmentChange = deriveAssignmentChange(item.changes);
            normalized.assignedToName = assignmentChange.assignedToName;
            normalized.assignmentNames = assignmentChange.assignmentNames;
            normalized.actionLabelKey = assignmentChange.actionLabelKey;
          }

          if (item.event_type === 'WORKLOG' && item.changes) {
            const worklogChange = deriveWorklogChange(item.changes);
            normalized.worklogValue = worklogChange.worklogValue;
            normalized.actionLabelKey = worklogChange.actionLabelKey;
          }

          if (item.event_type === 'COMMENT_REPLY' && item.changes) {
            normalized.replyText = itemCommentChange.text;
            normalized.commentText = itemCommentChange.text;
            normalized.replyToText = item.parent_comment || (!itemCommentChange.isRemoved ? item.changes.old_value || '' : '');
            if (itemCommentChange.actionLabelKey) {
              normalized.actionLabelKey = itemCommentChange.actionLabelKey;
            }
          }

          if (item.event_type === 'COMMENT') {
            normalized.commentText = itemCommentChange.text;
            if (itemCommentChange.actionLabelKey) {
              normalized.actionLabelKey = itemCommentChange.actionLabelKey;
            }
          }

          if (item.event_type === 'COMMENT_MENTION' || item.event_type === 'DESC_MENTION') {
            normalized.actionTargetTextKey = item.event_type === 'COMMENT_MENTION' 
              ? 'notifications.actions.inComment' 
              : 'notifications.actions.inDescription';
          }

          if (item.event_type === 'SPRINT_CREATED') {
            normalized.actionTargetText = item.sprint_name || item.message || '';
            normalized.sprintStartDate = item.sprint_start_date;
            normalized.sprintEndDate = item.sprint_end_date;
          }

          return [normalized];
        })
      };

      if (isReminderGroup) {
        return baseGroup.notifications.map(notif => ({
          ...baseGroup,
          id: notif.id,
          title: notif.message || baseGroup.title,
          notifications: [notif]
        }));
      }

      return [baseGroup];
      });
    }
    
    // Fallback to grouping flat items
    return groupNotificationsByTicket(activeItems);
  }, [activeItems, t, user]);

  const handleTabChange = (event, value) => {
    setActiveTab(value);
  };

  const handleGroupByChange = (event) => {
    setGroupBy(event.target.value);
  };

  const handleSortByChange = (event) => {
    setSortBy(event.target.value);
  };

  const handleClearFilters = () => {
    setSelectedProject(FILTER_VALUES.ALL);
    setAssigneeFilter('all');
    setStartDate('');
    setDueDate('');
    setSortBy(NOTIFICATION_SCREEN.SORT_OPTIONS[0].value);
    setGroupBy(NOTIFICATION_SCREEN.GROUP_BY_OPTIONS[0].value);
  };


  const handleDateFilterFocus = (event) => {
    if (!hasShownDateFilterInfo) {
      event.preventDefault();
      const targetInput = event.target;
      setPendingDateInputRef(targetInput);
      setShowDateFilterInfoDialog(true);
      setHasShownDateFilterInfo(true);
    }
  };

  const handleCloseDateFilterInfo = () => {
    const inputRef = pendingDateInputRef;
    setShowDateFilterInfoDialog(false);
    setPendingDateInputRef(null);
    
    // Open the date picker immediately after state update
    if (inputRef) {
      // Use multiple animation frames to ensure dialog is closed
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try {
            if (inputRef.showPicker) {
              inputRef.showPicker();
            }
            if (typeof inputRef.focus === 'function') inputRef.focus();
            if (typeof inputRef.click === 'function') inputRef.click();
          } catch (error) {
            // Silently fail if picker cannot be opened
          }
        });
      });
    }
  };

  const toggleExpandedTextRow = useCallback((key) => {
    setExpandedTextRows((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  const clearCompletedNotifications = useCallback((notificationIds = []) => {
    if (!Array.isArray(notificationIds) || notificationIds.length === 0) return;

    setCompletedNotifications((prev) => {
      const next = new Set(prev);
      notificationIds.forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  const getLoadedUnreadNotificationIds = useCallback((notifications = []) => {
    const ids = new Set();
    const stack = Array.isArray(notifications) ? [...notifications] : [];

    while (stack.length > 0) {
      const notification = stack.pop();
      if (!notification) continue;

      if (Array.isArray(notification.bulkActions) && notification.bulkActions.length > 0) {
        stack.push(...notification.bulkActions);
      }

      const notificationId = notification.id;
      if (!notificationId || String(notificationId).startsWith('summary-')) continue;
      if (notification.isRead || completedNotifications.has(notificationId)) continue;

      ids.add(notificationId);
    }

    return Array.from(ids);
  }, [completedNotifications]);

  const handleMarkSingleRead = async (notificationId) => {
    if (!notificationId) return;
    if (completedNotifications.has(notificationId)) return;

    // Immediately show as checked (optimistic UI)
    setCompletedNotifications(prev => new Set([...prev, notificationId]));

    try {
      await dispatch(markNotificationsRead({ notificationIds: notificationId, is_read: true })).unwrap();
      dispatch(applyNotificationReadState({ notificationIds: [notificationId], is_read: true }));
      setUndoSnackbar({ open: true, ids: [notificationId], count: 1 });
    } catch (error) {
      // Uncheck on failure
      setCompletedNotifications(prev => {
        const next = new Set(prev);
        next.delete(notificationId);
        return next;
      });
      toast.error(error || 'Failed to mark notification as read');
    }
  };

  const handleOpenTicket = useCallback(async (group) => {
    const ticketNumber = group?.ticketNumber;
    const ticketId = group?.id || group?.ticket_id || group?.ticketId;
    if (ticketNumber) {
      window.open(`/task/${encodeURIComponent(ticketNumber)}`, '_blank');
    }

    if (activeTab !== NOTIFICATION_SCREEN.TABS.UNREAD) return;

    const notificationIds = getLoadedUnreadNotificationIds(group?.notifications);
    if (notificationIds.length === 0) return;

    dispatch(applyNotificationReadState({ notificationIds, is_read: true }));

    try {
      const result = await dispatch(markNotificationsRead({ ticketId, notificationIds, is_read: true })).unwrap();
      const updatedCount = Number(result?.data?.updated_count);
      const count = Number.isFinite(updatedCount) ? updatedCount : notificationIds.length;
      setUndoSnackbar({ open: false, ids: [], count });
    } catch (error) {
      dispatch(applyNotificationReadState({ notificationIds, is_read: false }));
      toast.error(error?.message || error || 'Failed to mark notifications as read');
    }
  }, [activeTab, dispatch, getLoadedUnreadNotificationIds]);

  const handleOpenReadConfirm = (ids, context = { scope: 'page' }) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    previousCompletedRef.current = new Set(completedNotifications);
    setCompletedNotifications(new Set(ids));
    setPendingReadIds(ids);
    setPendingReadContext(context);
    setIsReadConfirmOpen(true);
  };

  const handleCloseReadConfirm = () => {
    setIsReadConfirmOpen(false);
    setPendingReadIds([]);
    setPendingReadContext({ scope: 'page' });
    setIsReadConfirmLoading(false);
    if (previousCompletedRef.current) {
      setCompletedNotifications(previousCompletedRef.current);
      previousCompletedRef.current = null;
    }
  };

  const handleConfirmRead = async () => {
    if (!pendingReadIds.length) {
      handleCloseReadConfirm();
      return;
    }
    
    const idsToMark = [...pendingReadIds];
    const count = idsToMark.length;
    const currentItemsCount = activeItems.length;
    
    // Close dialog immediately
    setIsReadConfirmOpen(false);
    setPendingReadIds([]);
    setPendingReadContext({ scope: 'page' });
    setIsReadConfirmLoading(false);
    previousCompletedRef.current = null;
    
    try {
      await dispatch(markNotificationsRead({ notificationIds: idsToMark, is_read: true })).unwrap();
      dispatch(applyNotificationReadState({ notificationIds: idsToMark, is_read: true }));
      setUndoSnackbar({ open: true, ids: idsToMark, count });
      
      // If all current items were marked as read and there are more pages, fetch next page
      if (activeTab === NOTIFICATION_SCREEN.TABS.UNREAD) {
        const pagination = paginationByTab?.[activeTab];
        // Check if we marked all visible items and there are more pages
        if (count >= currentItemsCount && pagination?.hasMore) {
          fetchNotifications(1, false);
        }
      }
    } catch (error) {
      // Revert optimistic UI update on failure
      setCompletedNotifications(new Set());
      toast.error(error?.message || error || 'Failed to mark notifications as read');
    }
  };

  const formatNotificationTime = useCallback((timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const created = new Date(timestamp);
    const diffMs = now.getTime() - created.getTime();
    if (Number.isNaN(diffMs)) return '';

    if (diffMs < 0) return 'Just now'; // Handle future dates

    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} min ago`;

    // Use UTC to avoid timezone issues
    const createdDateUTC = new Date(created.getUTCFullYear(), created.getUTCMonth(), created.getUTCDate());
    const todayUTC = new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const yesterdayUTC = new Date(todayUTC);
    yesterdayUTC.setDate(yesterdayUTC.getDate() - 1);

    if (createdDateUTC.getTime() === todayUTC.getTime()) return 'Today';
    if (createdDateUTC.getTime() === yesterdayUTC.getTime()) return 'Yesterday';

    const day = created.getDate();
    const month = created.toLocaleString('en-US', { month: 'short' });
    return created.getFullYear() !== now.getFullYear() 
      ? `${day} ${month} ${created.getFullYear()}` 
      : `${day} ${month}`;
  }, []);


  const fetchNotifications = useCallback((page, append = false, clearFilters = false, overrides = {}) => {
    if (!userId) return;
    const nextTab = overrides.tab || activeTab;
    const nextGroupBy = overrides.groupBy || groupBy;
    const nextSortBy = overrides.sortBy || sortBy;
    const nextOrder = overrides.order || (nextSortBy === NOTIFICATION_SORT.OLDEST ? 'ASC' : 'DESC');
    const isUnreadTab = nextTab === NOTIFICATION_SCREEN.TABS.UNREAD;
    const nextProject = overrides.projectId ?? selectedProject;

    // Clear filters on reload (when clearFilters is true)
    const params = {
      userId,
      tab: nextTab,
      unreadOnly: isUnreadTab ? true : false,
      page,
      limit: PAGINATION.DEFAULT_LIMIT,
      order: nextOrder,
      groupBy: nextGroupBy,
      append
    };

    // Only add filters if not clearing
    if (!clearFilters) {
      if (nextProject !== FILTER_VALUES.ALL) params.projectId = nextProject;
      const nextAssignee = overrides.assigneeFilter ?? assigneeFilter;
      const nextStartDate = overrides.startDate ?? startDate;
      const nextDueDate = overrides.dueDate ?? dueDate;

      if (nextAssignee !== 'all') params.assignee = nextAssignee;
      if (nextStartDate) params.startDate = nextStartDate;
      if (nextDueDate) params.endDate = nextDueDate;
    }

    dispatch(fetchNotificationsByUser(params));
  }, [activeTab, assigneeFilter, dispatch, dueDate, groupBy, selectedProject, sortBy, startDate, userId]);

  useEffect(() => {
    if (activeTab !== NOTIFICATION_SCREEN.TABS.UNREAD) return;
    const pagination = paginationByTab?.[activeTab];
    if (activeItems.length > 0 || pagination?.loading || pagination?.page == null) return;
    // An empty response with total_pages: 0 is a completed result, not a reason to retry.
    // This is especially important when the global unread badge still has notifications
    // that are excluded by the current project or other filters.
    if (!pagination?.hasMore || pendingBaselineResetRef.current) return;
    if (currentUnreadCount > 0) fetchNotifications(1, false);
  }, [activeItems.length, activeTab, currentUnreadCount, fetchNotifications, paginationByTab]);

  useEffect(() => {
    if (!userId) return;
    const pagination = paginationByTab?.[activeTab];
    if (pagination?.loading) return; // Prevent calling if already loading
    
    pendingBaselineResetRef.current = true;
    dispatch(resetNotificationsTab(activeTab));
    fetchNotifications(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, assigneeFilter, dueDate, dispatch, groupBy, selectedProject, sortBy, startDate, userId]);

  useEffect(() => {
    const handleNotificationEvent = (event) => {
      const { newNotificationCount } = event?.detail || {};

      if (pendingBaselineResetRef.current) {
        return;
      }

      const nextNewCount = typeof newNotificationCount === 'number'
        ? Math.max(0, newNotificationCount)
        : 0;

      if (nextNewCount > 0) {
        setNewSinceLastLoad(prev => prev + nextNewCount);
        setShowNewBanner(true);
      }
    };

    window.addEventListener('pmpi:new-notification', handleNotificationEvent);
    return () => {
      window.removeEventListener('pmpi:new-notification', handleNotificationEvent);
    };
  }, []);

  useEffect(() => {
    if (projectDropdownsFetchedRef.current || projectDropdownsByUserLoading) return;
    if (Array.isArray(projectDropdownsByUser) && projectDropdownsByUser.length > 0) {
      projectDropdownsFetchedRef.current = true;
      return;
    }

    projectDropdownsFetchedRef.current = true;
    dispatch(fetchProjectDropdownsByUser());
  }, [dispatch, projectDropdownsByUser, projectDropdownsByUserLoading]);

  useEffect(() => {
    const pagination = paginationByTab?.[activeTab];
    if (!pendingBaselineResetRef.current) return;
    if (!pagination || pagination.loading) return;
    setShowNewBanner(false);
    setNewSinceLastLoad(0);
    pendingBaselineResetRef.current = false;
  }, [activeTab, paginationByTab]);

  useEffect(() => {
    const fetchAssignees = async () => {
      try {
        const response = await api.get(ENDPOINTS.TIMESHEET_DROPDOWNS('users'));
        const users = response.data?.data || [];
        setAssigneeOptions(users.map(user => ({
          id: user.id,
          name: user.name
        })));
      } catch (error) {
        console.error('Failed to fetch assignees:', error);
      }
    };
    fetchAssignees();
  }, []);

  const handleLoadMore = useCallback(() => {
    const pagination = paginationByTab?.[activeTab];
    if (!pagination || pagination.loading || !pagination.hasMore) return;
    if (pagination.page === 1 && activeItems.length === 0) return;
    fetchNotifications(pagination.page + 1, true);
  }, [activeItems.length, activeTab, fetchNotifications, paginationByTab]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          handleLoadMore();
        }
      },
      { root: null, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [handleLoadMore]);

  const handleNewNotificationsClick = useCallback(() => {
    setActiveTab(DEFAULT_NOTIFICATION_TAB);
    setSelectedProject(FILTER_VALUES.ALL);
    setGroupBy(DEFAULT_NOTIFICATION_GROUP_BY);
    setSortBy(DEFAULT_NOTIFICATION_SORT);
    setAssigneeFilter('all');
    setStartDate('');
    setDueDate('');
    pendingBaselineResetRef.current = true;
    dispatch(resetNotificationsTab(DEFAULT_NOTIFICATION_TAB));
  }, [dispatch]);

  const renderContentRow = (rowId, content, createdAt, { isBulk = false, isLastBulk = false, showTime = true, isSummary = false, hideCheckbox = false } = {}) => {
    // Only apply completed styling if we are actually in the unread tab
    const isCompleted = activeTab === NOTIFICATION_SCREEN.TABS.UNREAD && completedNotifications.has(rowId);
    // Show checkbox for all rows in unread tab except summary rows (which have synthetic IDs)
    const showCheckboxFlag = !isSummary && !hideCheckbox && activeTab === NOTIFICATION_SCREEN.TABS.UNREAD;
    
    const rowContent = (
      <div className={`notifications-row${isBulk ? ' notifications-row--bulk' : ''}${isCompleted ? ' has-checkbox-visible' : ''}${!showCheckboxFlag ? ' no-checkbox' : ''}`}>
        {showCheckboxFlag && (
          <div 
            className={`notifications-row-checkbox${isCompleted ? ' checked' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleMarkSingleRead(rowId);
            }}
          >
            {isCompleted && <Check />}
          </div>
        )}
        <Typography className="notifications-row-text">
          {content}
        </Typography>
        {showTime && (
          <span className="notifications-row-time">
            {formatNotificationTime(createdAt)}
          </span>
        )}
      </div>
    );

    if (!isBulk) return rowContent;

    return (
      <div key={rowId} className="notifications-bulk-row">
        <span className={`notifications-bulk-branch${isLastBulk ? ' is-last' : ''}`} />
        <div className="notifications-bulk-box">
          {rowContent}
        </div>
      </div>
    );
  };

  const renderNotificationRow = (notification, options = {}) => (
    renderContentRow(
      notification.id,
      renderNotificationMessage(notification, { isCompact: options.isBulk, t, currentUserId: userId, mentionUsers }),
      notification.createdAt,
      options
    )
  );

  const renderExpandableText = useCallback((text, key, {
    quoted = true,
    className = 'notifications-detail-text',
    lineClamp = 1
  } = {}) => {
    const normalizedText = String(text || '').trim();
    if (!normalizedText) return null;

    const isExpanded = Boolean(expandedTextRows[key]);
    const displayText = quoted ? `“${normalizedText}”` : normalizedText;

    return (
      <ClampedText
        className={className}
        lineClamp={lineClamp}
        isExpanded={isExpanded}
        onToggle={() => toggleExpandedTextRow(key)}
        t={t}
      >
        {displayText}
      </ClampedText>
    );
  }, [expandedTextRows, t, toggleExpandedTextRow]);

  const renderNotificationTextBlock = useCallback((text, key, {
    quoted = true,
    className = 'notifications-detail-text',
    lineClamp = 1
  } = {}) => {
    const normalizedText = String(text || '').trim();
    if (!normalizedText) return null;

    if (hasNotificationHtmlContent(normalizedText)) {
      return (
        <ClampedText
          className={`${className} notifications-rich-text`}
          lineClamp={lineClamp}
          isExpanded={Boolean(expandedTextRows[key])}
          onToggle={() => toggleExpandedTextRow(key)}
          t={t}
        >
          {renderNotificationTextContent(normalizedText, {
            mentionUsers,
            quoted,
            className: `${className} notifications-rich-text`
          })}
        </ClampedText>
      );
    }

    return renderExpandableText(normalizedText, key, { quoted, className, lineClamp });
  }, [expandedTextRows, mentionUsers, renderExpandableText, t, toggleExpandedTextRow]);

  const renderDetailRow = (parentId, text, { isLast = false } = {}) => (
    <div key={`${parentId}-detail-${text?.slice(0, 16)}`} className="notifications-bulk-row">
      <span className={`notifications-bulk-branch${isLast ? ' is-last' : ''}`} />
      <div className="notifications-bulk-box">
        <div className="notifications-row notifications-row--detail">
          <Typography className="notifications-row-text">
            {renderNotificationTextBlock(text, `${parentId}-detail`)}
          </Typography>
        </div>
      </div>
    </div>
  );


  const renderNameToken = useCallback((name, key) => (
    <span key={key} className="notifications-actor-token">
      <span className="notifications-actor-box">{getInitial(name)}</span>
      <span className="notifications-actor-name">{name}</span>
    </span>
  ), []);

  const renderCommentSummaryRow = (group) => {
    const latestTimestamp = group.notifications.reduce((latest, notification) => {
      const time = new Date(notification.createdAt).getTime();
      if (Number.isNaN(time)) return latest;
      return latest ? Math.max(latest, time) : time;
    }, null);
    const summary = buildCommentSummary(group);
    if (!summary.hasReplies) {
      return null;
    }

    return renderContentRow(
      group.id,
      <span className="notifications-message">
        {summary.replyNames.length === 1 && (
          renderNameToken(summary.replyNames[0], `${group.id}-reply-actor`)
        )}
        <span className="notifications-action-chip">
          {summary.replyNames.length > 1
            ? t('notifications.commentSummary.action')
            : t('notifications.actions.replied')}
        </span>
        <span className="notifications-inline-label">{t('notifications.commentSummary.on')}</span>
        {renderNotificationTextBlock(summary.commentText, `${group.id}-summary`, {
          className: 'notifications-comment-text',
          lineClamp: 1
        })}
      </span>,
      latestTimestamp ? new Date(latestTimestamp).toISOString() : group.notifications[0]?.createdAt,
      { isBulk: false, showTime: true, isSummary: true }
    );
  };

  const renderCommentReplyRow = (notification, { isLast } = {}) => {
    const authorName = notification?.actorName || t('notifications.commentSummary.authorFallback');
    const replyText = notification?.replyText || notification?.commentText || '';
    const replyActionLabel = notification?.actionLabelKey === 'notifications.actions.commentRemoved'
      ? t('notifications.actions.commentRemoved')
      : t('notifications.commentSummary.action');

    return renderContentRow(
      notification.id,
      <span className="notifications-message notifications-message--compact">
        {renderNameToken(authorName, `${notification.id}-author`)}
        <span className="notifications-action-chip">{replyActionLabel}</span>
        {replyText && renderNotificationTextBlock(replyText, `${notification.id}-reply`, {
          className: 'notifications-comment-text',
          lineClamp: 1
        })}
      </span>,
      notification.createdAt,
      { isBulk: true, isLastBulk: isLast, showTime: false }
    );
  };

  const getGroupNotificationIds = useCallback((group) => {
    if (!group || !Array.isArray(group.notifications)) return [];

    return group.notifications.reduce((ids, notification) => {
      const notificationId = notification?.id;
      if (!notificationId || notificationId.toString().startsWith('summary-')) {
        return ids;
      }

      ids.push(notificationId);
      return ids;
    }, []);
  }, []);

  const handleUndoRead = async () => {
    const idsToUndo = [...undoSnackbar.ids];
    if (idsToUndo.length === 0) return;

    setUndoSnackbar({ open: false, ids: [], count: 0 });
    clearCompletedNotifications(idsToUndo);

    try {
      await dispatch(markNotificationsRead({ notificationIds: idsToUndo, is_read: false })).unwrap();
      dispatch(applyNotificationReadState({ notificationIds: idsToUndo, is_read: false }));
    } catch (error) {
      toast.error(error || 'Failed to undo mark as read');
    }
  };

  const handleCloseUndoSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setUndoSnackbar({ open: false, ids: [], count: 0 });
  };

  const filteredGroups = sourceGroups;
  const isTicketLevelReadConfirm = pendingReadContext?.scope === 'ticket';
  const pendingReadMessage = isTicketLevelReadConfirm
    ? t('notifications.confirmLoadedTicketReadMessage', { count: pendingReadIds.length })
    : t('notifications.confirmReadMessage', { count: pendingReadIds.length });

  useLayoutEffect(() => {
    const updateScrollRegionHeight = () => {
      const mainEl = mainRef.current;
      const fixedEl = fixedSectionRef.current;

      if (!mainEl || !fixedEl) return;

      const mainStyles = window.getComputedStyle(mainEl);
      const mainGap = parseFloat(mainStyles.rowGap || mainStyles.gap || '0') || 0;
      const nextHeight = Math.max(
        0,
        mainEl.clientHeight - fixedEl.offsetHeight - mainGap
      );

      setScrollRegionHeight(nextHeight);
    };

    updateScrollRegionHeight();

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
          updateScrollRegionHeight();
        })
      : null;

    if (resizeObserver && mainRef.current) resizeObserver.observe(mainRef.current);
    if (resizeObserver && fixedSectionRef.current) resizeObserver.observe(fixedSectionRef.current);

    window.addEventListener('resize', updateScrollRegionHeight);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateScrollRegionHeight);
    };
  }, [showNewBanner, activeTab]);

  return (
    <div className="dashboard">
      <Navbar />

      <main ref={mainRef} className="main notifications-screen">
        <div ref={fixedSectionRef} className="notifications-fixed-section">
          <div className="notifications-header">
            <div className="notifications-header-left">
              <IconButton
                className="notifications-back-button"
                onClick={() => navigate(-1)}
                aria-label={t('dashboard.title')}
              >
                <ArrowBackIosNew fontSize="small" />
              </IconButton>
              <Typography
                variant="h6"
                className="notifications-title"
              >
                {t('notifications.title')}
              </Typography>
            </div>
          </div>


          <div className="notifications-toolbar">
            <div className="notifications-toolbar-row">
              <div className="notifications-tabs-with-filter">
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  className="notifications-tabs"
                >
                  <Tab
                    value={NOTIFICATION_SCREEN.TABS.UNREAD}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {t('notifications.tabs.unread')}
                        {currentUnreadCount > 0 && (
                          <Chip
                            label={currentUnreadCount}
                            size="small"
                            sx={{
                              bgcolor: COLORS.gray[100],
                              color: COLORS.gray[600],
                              fontSize: '11px',
                              fontWeight: 600,
                              height: 20,
                              minWidth: 24
                            }}
                          />
                        )}
                      </Box>
                    }
                    className="notifications-tab"
                  />
                  <Tab
                    value={NOTIFICATION_SCREEN.TABS.CLEARED}
                    label={t('notifications.tabs.cleared')}
                    className="notifications-tab"
                  />
                </Tabs>
                <FilterHeader
                  viewMode="notifications"
                  selectedProject={selectedProject}
                  selectedSort={sortBy}
                  selectedAssignee={assigneeFilter}
                  startDate={startDate}
                  dueDate={dueDate}
                  onProjectChange={(e) => setSelectedProject(e.target.value)}
                  onSortChange={handleSortByChange}
                  onAssigneeChange={(e) => setAssigneeFilter(e.target.value)}
                  onStartDateChange={(e) => setStartDate(e.target.value)}
                  onDueDateChange={(e) => setDueDate(e.target.value)}
                  onClearFilters={handleClearFilters}
                  onDateFilterFocus={handleDateFilterFocus}
                  projectDropdownsByUser={projectDropdownsByUser}
                  dropdowns={{ assignees: assigneeOptions }}
                  dropdownsLoading={projectDropdownsByUserLoading}
                />
              </div>

              <div className="notifications-controls">
                {activeTab === NOTIFICATION_SCREEN.TABS.UNREAD && (
                  <div className="notifications-readall-container">
                    <button
                      type="button"
                      className="notifications-readall-text"
                      onClick={() => {
                        const allIds = [];
                        sourceGroups.forEach(group => {
                          group.notifications.forEach(notification => {
                            if (notification.id && !notification.id.toString().startsWith('summary-')) {
                              allIds.push(notification.id);
                            }
                          });
                        });
                        if (allIds.length > 0) {
                          handleOpenReadConfirm(allIds);
                        }
                      }}
                    >
                      {t('notifications.markAllReadText')}
                    </button>
                  </div>
                )}
                {activeTab === NOTIFICATION_SCREEN.TABS.CLEARED && (
                  <div className="notifications-readall-container">
                    <Tooltip
                      title={t('notifications.thirtyDaysInfo')}
                      arrow
                      placement="top"
                      slotProps={{ tooltip: { className: 'app-tooltip' } }}
                    >
                      <InfoOutlined
                        className="notifications-info-icon"
                        fontSize="small"
                      />
                    </Tooltip>
                  </div>
                )}
                <FormControl className="filter-select notifications-control-select notifications-groupby-inline" size="medium">
                  <Select
                    value={groupBy}
                    onChange={handleGroupByChange}
                    className="notifications-groupby-input"
                    displayEmpty
                    renderValue={(selected) => {
                      const selectedOption = NOTIFICATION_SCREEN.GROUP_BY_OPTIONS.find(
                        option => option.value === selected
                      );
                      const selectedLabel = selectedOption ? t(selectedOption.labelKey) : t('notifications.groupBy.label', 'Group by');
                      return (
                        <span className="notifications-groupby-selected">
                          <img className="notifications-groupby-icon" src={groupByIcon} alt="" />
                          {selectedLabel}
                        </span>
                      );
                    }}
                  >
                    {NOTIFICATION_SCREEN.GROUP_BY_OPTIONS.map(option => {
                      const fullLabel = t(option.labelKey);
                      const shortLabel = fullLabel.replace(/^Group By\s*/i, 'By ');
                      return (
                        <MenuItem key={option.value} value={option.value}>
                          {shortLabel}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>

          {showNewBanner && (
            <button
              type="button"
              className="notifications-new-banner"
              onClick={handleNewNotificationsClick}
            >
              <span className="notifications-new-banner-text">
                {t('notifications.newBanner', '{{count}} new notifications', {
                  count: newSinceLastLoad
                })}
              </span>
              <span className="notifications-new-banner-action">
                {t('notifications.newBannerAction', 'Reload')}
              </span>
            </button>
          )}
        </div>

        <div
          className="notifications-scroll-region"
          style={{ height: scrollRegionHeight > 0 ? `${scrollRegionHeight}px` : undefined }}
        >
          <div className="notifications-content">
          {paginationByTab[activeTab]?.loading && activeItems.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <Typography>{t('notifications.loading')}</Typography>
            </Box>
          ) : filteredGroups.length === 0 ? (
            <div className="notifications-empty-state">
              <div className="notifications-empty-card">
                <span className="notifications-empty-icon">
                  <NotificationsNone />
                </span>
                {activeTab === NOTIFICATION_SCREEN.TABS.UNREAD ? (
                  <>
                    <Typography className="notifications-empty-title">
                      {t('notifications.emptyUnread.title', "You're all caught up! 🎉 Nothing new here")}
                    </Typography>
                    <Typography className="notifications-empty-description">
                      {t('notifications.emptyUnread.subtitle', 'No unread notifications right now.')}
                    </Typography>
                    <Typography className="notifications-empty-description">
                      {t('notifications.emptyUnread.helper', 'Check the Cleared tab to view past updates.')}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography className="notifications-empty-title">
                      {t('notifications.emptyCleared.title', 'You haven’t received any notifications yet.')}
                    </Typography>
                    <Typography className="notifications-empty-description">
                      {t('notifications.emptyCleared.subtitle', 'We’ll notify you when something arrives.')}
                    </Typography>
                  </>
                )}
              </div>
            </div>
          ) : (
            filteredGroups.map(group => (
              <Card key={group.id} elevation={0} className="notifications-card">
                <CardContent className="notifications-card-content">
                  {(() => {
                    if (group.isSprintGroup || group.isReminderGroup) return null;
                    const groupNotificationIds = getGroupNotificationIds(group);
                    const hasBulkSelection = groupNotificationIds.length > 0;
                    const isGroupCompleted = hasBulkSelection
                      && groupNotificationIds.every((notificationId) => completedNotifications.has(notificationId));

                    return (
                      <>
                        <div className="notifications-card-header">
                          {activeTab === NOTIFICATION_SCREEN.TABS.UNREAD && hasBulkSelection ? (
                            <button
                              type="button"
                              className={`notifications-ticket-checkbox${isGroupCompleted ? ' checked' : ''}`}
                              onClick={() => handleOpenReadConfirm(groupNotificationIds, { scope: 'ticket', ticketId: group.id })}
                              aria-label={t('notifications.markTicketRead', 'Mark all notifications for this ticket as read')}
                            >
                              {isGroupCompleted && <Check />}
                            </button>
                          ) : (
                            <div className="notifications-ticket-box" />
                          )}
                          <Typography 
                            className="notifications-card-title"
                            onClick={() => handleOpenTicket(group)}
                          >
                            {group.ticketNumber ? `${group.ticketNumber}: ` : ''}{group.title}
                          </Typography>
                        </div>
                        <div className="notifications-card-separator" />
                      </>
                    );
                  })()}
                  <div className="notifications-card-rows">
                    {(() => {
                      const threadMap = new Map();
                      group.notifications.forEach((notification, index) => {
                        if (!COMMENT_EVENT_TYPES.has(notification?.event_type)) return;
                        const threadKey = getCommentThreadKey(notification);
                        if (!threadKey) return;
                        if (!threadMap.has(threadKey)) {
                          threadMap.set(threadKey, {
                            id: threadKey,
                            firstIndex: index,
                            notifications: []
                          });
                        }
                        threadMap.get(threadKey).notifications.push(notification);
                      });

                      return group.notifications.map((notification, index) => {
                        const bulkActions = Array.isArray(notification?.bulkActions) ? notification.bulkActions : [];
                        
                        // Handle summary notification (for comment replies)
                        if (notification?.isSummary) {
                          const hasSingleReplyActor = notification?.actorName && Number(notification?.replyCount) === 1;
                          return (
                            <React.Fragment key={notification.id}>
                              {renderContentRow(
                                notification.id,
                                <span className="notifications-message">
                                  {hasSingleReplyActor && renderNameToken(notification.actorName, `${notification.id}-reply-actor`)}
                                  <span className="notifications-action-chip">
                                    {Number(notification?.replyCount) > 1
                                      ? t('notifications.commentSummary.action')
                                      : t('notifications.actions.replied')}
                                  </span>
                                  <span className="notifications-inline-label">{t('notifications.commentSummary.on')}</span>
                                  {renderNotificationTextBlock(notification.commentText, `${notification.id}-summary`, {
                                    className: 'notifications-comment-text',
                                    lineClamp: 1
                                  })}
                                </span>,
                                notification.createdAt,
                                { isBulk: false, showTime: true, isSummary: true }
                              )}
                            </React.Fragment>
                          );
                        }

                        // Handle comment replies (should be rendered as bulk/indented)
                        if (notification?.event_type === NOTIFICATION_EVENT_TYPES.COMMENT_REPLY && !notification?.isSummary) {
                          const isLast = index === group.notifications.length - 1 || 
                                        group.notifications[index + 1]?.event_type !== NOTIFICATION_EVENT_TYPES.COMMENT_REPLY;
                          return (
                            <React.Fragment key={notification.id}>
                              {renderCommentReplyRow(notification, { isLast })}
                            </React.Fragment>
                          );
                        }

                        const isCommentThreadEvent = COMMENT_EVENT_TYPES.has(notification?.event_type);

                        if (isCommentThreadEvent) {
                          const threadKey = getCommentThreadKey(notification);
                          const thread = threadMap.get(threadKey);
                          if (!thread || thread.firstIndex !== index) {
                            return null;
                          }

                          const summaryGroup = {
                            id: thread.id,
                            title: group.title,
                            notifications: thread.notifications
                          };
                          const summaryRow = renderCommentSummaryRow(summaryGroup);
                          const replyItems = thread.notifications
                            .filter(item => item?.event_type === NOTIFICATION_EVENT_TYPES.COMMENT_REPLY)
                            .map((item, replyIndex, list) => (
                              <React.Fragment key={item.id}>
                                {renderCommentReplyRow(item, { isLast: replyIndex === list.length - 1 })}
                              </React.Fragment>
                            ));

                          if (!summaryRow) {
                            const primaryNotification = thread.notifications.find(item => item?.event_type === NOTIFICATION_EVENT_TYPES.COMMENT)
                              || thread.notifications[0];
                            return (
                              <React.Fragment key={primaryNotification.id}>
                                {renderNotificationRow(primaryNotification)}
                                {primaryNotification?.event_type === NOTIFICATION_EVENT_TYPES.COMMENT && primaryNotification?.commentText && (
                                  renderDetailRow(primaryNotification.id, primaryNotification.commentText, { isLast: replyItems.length === 0 })
                                )}
                                {replyItems}
                              </React.Fragment>
                            );
                          }

                          return (
                            <React.Fragment key={`${thread.id}-summary`}>
                              {summaryRow}
                              {replyItems}
                            </React.Fragment>
                          );
                        }

                        if (notification.managerGroups && notification.managerGroups.length > 0) {
                          return (
                            <React.Fragment key={notification.id}>
                              {renderNotificationRow(notification)}
                              <div className="notifications-manager-groups-container">
                                {notification.managerGroups.map((group) => {
                                  const isExpanded = expandedManagerGroups[`${notification.id}-${group.assignee}`];
                                  return (
                                    <div key={group.assignee} className="notifications-assignee-card">
                                      <div className="notifications-assignee-header" onClick={() => toggleManagerGroup(`${notification.id}-${group.assignee}`)}>
                                        <span className="notifications-assignee-name">{group.assignee}</span>
                                        <ExpandMore style={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }} className="notifications-assignee-expand-icon" />
                                      </div>
                                      <Collapse in={isExpanded}>
                                        <div className="notifications-nested-group-custom">
                                          {group.tickets.map((action) => (
                                            <div key={action.id} className="notifications-ticket-row-custom">
                                              <span className="notifications-ticket-title-custom">
                                                <a 
                                                  href={`/${ENDPOINTS.TASK_URL(encodeURIComponent(action.ticketNumber))}`} 
                                                  target="_blank" 
                                                  rel="noopener noreferrer" 
                                                  onClick={(e) => e.stopPropagation()}
                                                  className="clickable-ticket-link"
                                                  style={{ marginRight: '4px' }}
                                                >
                                                  {action.ticketNumber}
                                                </a>
                                                {action.title}
                                              </span>
                                              <span className="notifications-ticket-missing-custom">
                                                ( {t('notifications.missing')}: {action.missingUpdates?.length ? action.missingUpdates.join(' & ') : t('notifications.commentAndWorklog')} )
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      </Collapse>
                                    </div>
                                  );
                                })}
                              </div>
                            </React.Fragment>
                          );
                        }

                        if (bulkActions.length === 0) {
                          const isComment = notification?.event_type === NOTIFICATION_EVENT_TYPES.COMMENT;
                          const isCommentReply = notification?.event_type === NOTIFICATION_EVENT_TYPES.COMMENT_REPLY;

                          return (
                            <React.Fragment key={notification.id}>
                              {renderNotificationRow(notification)}
                              {isComment && notification?.commentText && (
                                renderDetailRow(notification.id, notification.commentText, { isLast: true })
                              )}
                              {isCommentReply && notification?.replyText && (
                                renderDetailRow(notification.id, notification.replyText, { isLast: true })
                              )}
                            </React.Fragment>
                          );
                        }

                        const isReminder = notification.event_type === NOTIFICATION_EVENT_TYPES.STANDUP_NOT_UPDATED_REMINDER || notification.event_type === NOTIFICATION_EVENT_TYPES.TASK_STAGNATION_REMINDER;
                        
                        return (
                          <React.Fragment key={notification.id}>
                            {renderNotificationRow(notification)}
                            {isReminder ? (
                              <div className="notifications-nested-group-custom notifications-user-view-group">
                                {bulkActions.map((action, actionIndex) => (
                                  <div key={action.id || `${notification.id}-bulk-${actionIndex}`} className="notifications-ticket-row-custom">
                                    <span className="notifications-ticket-title-custom">
                                      <a 
                                        href={`/${ENDPOINTS.TASK_URL(encodeURIComponent(action.ticketNumber))}`} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        onClick={(e) => e.stopPropagation()}
                                        className="clickable-ticket-link"
                                        style={{ marginRight: '4px' }}
                                      >
                                        {action.ticketNumber}
                                      </a>
                                      {action.title}
                                    </span>
                                    <span className="notifications-ticket-missing-custom">
                                      ( {t('notifications.missing')}: {action.missingUpdates?.length ? action.missingUpdates.join(' & ') : t('notifications.commentAndWorklog')} )
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              bulkActions.map((action, actionIndex) => (
                                <React.Fragment key={action.id || `${notification.id}-bulk-${actionIndex}`}>
                                    {renderNotificationRow(action, {
                                      isBulk: true,
                                      isLastBulk: actionIndex === bulkActions.length - 1,
                                      showTime: false,
                                      hideCheckbox: true
                                    })}
                                </React.Fragment>
                              ))
                            )}
                          </React.Fragment>
                        );
                      });
                    })()}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
          <div ref={loadMoreRef} className="notifications-load-more" />
          </div>
        </div>
        <Dialog
          open={isReadConfirmOpen}
          onClose={handleCloseReadConfirm}
          PaperProps={{ className: 'task-move-confirm-dialog' }}
        >
          <DialogTitle className="task-move-confirm-title">
            <Box className="task-move-confirm-title-row">
              <Avatar className="task-move-confirm-icon">
                <DoneAll />
              </Avatar>
              <Box className="task-move-confirm-text">
                <Typography variant="h6" className="task-move-confirm-heading">
                  {t('notifications.markAllReadText')}
                </Typography>
                <Typography variant="body2" className="task-move-confirm-subtitle">
                  {t('notifications.confirmReadSubtitle')}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent className="task-move-confirm-content">
            <Typography variant="body1" className="task-move-confirm-message">
              {pendingReadMessage}
            </Typography>
          </DialogContent>
          <DialogActions className="task-move-confirm-actions">
            <Button
              onClick={handleCloseReadConfirm}
              disabled={isReadConfirmLoading}
              className="task-move-confirm-cancel"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleConfirmRead}
              variant="contained"
              disableElevation
              disabled={isReadConfirmLoading}
              className="task-move-confirm-submit"
            >
              {t('notifications.markAllReadText')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showDateFilterInfoDialog}
          onClose={handleCloseDateFilterInfo}
          PaperProps={{ className: 'task-move-confirm-dialog' }}
        >
          <DialogTitle className="task-move-confirm-title">
            <Box className="task-move-confirm-title-row">
              <Avatar className="task-move-confirm-icon">
                <InfoOutlined />
              </Avatar>
              <Box className="task-move-confirm-text">
                <Typography variant="h6" className="task-move-confirm-heading">
                  {t('notifications.dateFilterInfoTitle')}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent className="task-move-confirm-content">
            <Typography variant="body1" className="task-move-confirm-message">
              {t('notifications.dateFilterInfo')}
            </Typography>
          </DialogContent>
          <DialogActions className="task-move-confirm-actions">
            <Button
              onClick={handleCloseDateFilterInfo}
              variant="contained"
              disableElevation
              className="task-move-confirm-submit"
            >
              {t('common.clear')}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={undoSnackbar.open}
          autoHideDuration={NOTIFICATION_SCREEN.UNDO_TIMEOUT_MS}
          onClose={handleCloseUndoSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{
            '& .MuiSnackbarContent-root': {
              padding: 0,
              backgroundColor: 'transparent',
              boxShadow: 'none'
            }
          }}
        >
          <Box
            className="notifications-undo-toast"
            onClick={handleUndoRead}
          >
            <Box className="notifications-undo-icon-wrapper">
              <Undo className="notifications-undo-icon" />
            </Box>
            <Box className="notifications-undo-content">
              <Typography className="notifications-undo-title">
                {undoSnackbar.count === 1
                  ? t('notifications.markedAsRead', 'Marked 1 notification as read')
                  : t('notifications.markedAsReadMultiple', 'Marked {{count}} notifications as read', { count: undoSnackbar.count })}
              </Typography>
            </Box>
            <button
              className="notifications-undo-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleUndoRead();
              }}
            >
              {t('common.undo', 'Undo')}
            </button>
          </Box>
        </Snackbar>
      </main>
    </div>
  );
};

export default Notifications;
